import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Button from '@mui/material/Button';
import { Check, ClipboardList, QrCode } from 'lucide-react';
import { IdeaCard } from '../../../../components/ClientEntry/SmartIdeaDialog/IdeaKit';
import type { IntlLike } from '../clientPresentation';
import { applyQuizToRecruitProfile, quizMergeMessage, useRecruitQuiz } from '../recruitQuizStorage';
import { discLabel, discName, isDiscLetter } from '../../../../constants/disc';
import type { DiscLetter } from '../../../../constants/disc';

/**
 * 招募 IDEA 候選人概覽 — the 職涯測驗, the recruit twin of Smart IDEA's 保障小測驗
 * card: an invitation to send it while the candidate has not played it, and
 * once they have, what it found (type, route, picks) with one button that
 * copies it into the 招募檔案. The QR / link / full report stay in
 * RecruitQuizDialog, which 發送 / 查看 opens.
 */

export type RecruitQuizCardProps = {
  clientId?: string | null;
  intl: IntlLike;
  /** The DISC letter on file, to say whether the quiz agrees with it. */
  discOnFile?: string | null;
  /** 招募路線 labels (🎓 畢業生 / 💼 有經驗), from recruitIdeaPathCopy. */
  pathLabel: (path: 'graduate' | 'experienced') => string;
  onOpenQuiz?: () => void;
  /** Returns false when the change was not allowed. */
  onChangePersonality?: (letter: DiscLetter) => boolean | void;
};

const RecruitQuizCard = (props: RecruitQuizCardProps) => {
  const { clientId, intl, discOnFile, pathLabel, onOpenQuiz, onChangePersonality } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const quiz = useRecruitQuiz(clientId);
  const [mergeMessage, setMergeMessage] = useState<string | null>(null);
  // A message about one candidate's sync never shows on the next candidate's result.
  const quizId = quiz?.id;
  useEffect(() => setMergeMessage(null), [clientId, quizId]);
  if (!clientId) return null;
  const applied = mergeMessage !== null;

  const letter = quiz && isDiscLetter(quiz.discLetter) ? quiz.discLetter : null;
  const onFile = isDiscLetter(discOnFile) ? discOnFile : null;
  const agrees = !!letter && letter === onFile;

  const apply = () => {
    if (!quiz) return;
    // Safe merge: the letter on file wins, the quiz's only fills an empty one.
    setMergeMessage(
      quizMergeMessage(
        applyQuizToRecruitProfile(clientId, quiz, { currentDisc: discOnFile, onChangePersonality }),
        intl.locale
      )
    );
  };

  return (
    // The same section card as the rest of 招募 IDEA, and the same 發送 button
    // as Smart IDEA's 保障小測驗 card; the body keeps its own classes.
    <IdeaCard
      id="quiz"
      testId="recruit-quiz-card"
      icon={<ClipboardList size={18} aria-hidden="true" />}
      title={t('clientv2-recruit-quiz')}
      hint={quiz ? t('clientv2-recruit-quiz-card-done') : undefined}
      action={
        // Rendered inside IdeaCard's scope, so it needs none of its own.
        onOpenQuiz && (
          <Button
            size="small"
            variant="outlined"
            data-testid="recruit-quiz-card-open"
            startIcon={<QrCode size={14} aria-hidden="true" />}
            onClick={onOpenQuiz}
            sx={{ flexShrink: 0 }}
          >
            {t(quiz ? 'clientv2-recruit-quiz-card-resend' : 'clientv2-recruit-quiz-card-send')}
          </Button>
        )
      }
    >
      {!quiz ? (
        <p className="clientv2-quizcard-empty" data-testid="recruit-quiz-card-empty">
          {t('clientv2-recruit-quiz-card-empty')}
        </p>
      ) : (
        <div className="clientv2-quizcard-result" data-testid="recruit-quiz-card-result">
          <p className="clientv2-quizcard-meta">
            {t('clientv2-recruit-quiz-card-completed', { date: moment(quiz.completedAt).format('YYYY-MM-DD') })}
            {quiz.path && ` · ${pathLabel(quiz.path)}`}
          </p>

          {letter && (
            <div className="clientv2-quizcard-disc" data-testid="recruit-quiz-card-disc">
              <span className="clientv2-quizcard-label">{t('clientv2-recruit-quiz-card-disc')}</span>
              <strong>{discLabel(letter, intl.locale)}</strong>
              <span className="clientv2-quizcard-onfile">
                {t('clientv2-recruit-quiz-card-on-file', {
                  type: onFile ? discName(onFile, intl.locale) : t('clientv2-recruit-quiz-card-unset')
                })}
                {agrees && ' ✓'}
              </span>
            </div>
          )}

          <div className="clientv2-quizcard-counts">
            <span>{t('clientv2-recruit-quiz-card-pains', { n: quiz.painPoints.length })}</span>
            <span>{t('clientv2-recruit-quiz-card-concerns', { n: quiz.concerns.length })}</span>
            <span>{t('clientv2-recruit-quiz-card-strengths', { n: quiz.strengths.length })}</span>
          </div>

          <button
            type="button"
            className="clientv2-persona-set clientv2-quizcard-apply"
            data-testid="recruit-quiz-card-apply"
            onClick={apply}
          >
            {applied && <Check size={13} aria-hidden="true" />}
            {t(applied ? 'clientv2-recruit-quiz-card-applied' : 'clientv2-recruit-quiz-card-apply')}
          </button>
          {mergeMessage && (
            <p className="clientv2-quizcard-meta" data-testid="recruit-quiz-card-merge-message">
              {mergeMessage}
            </p>
          )}
        </div>
      )}
    </IdeaCard>
  );
};

export default RecruitQuizCard;
